/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Breadcrumb, IBreadcrumbItem, IBreadcrumbStyleProps, IBreadcrumbStyles } from '@fluentui/react/lib/Breadcrumb';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2, important } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import styled from 'styled-components';

type LanguageStudioSubHeaderProps = {
    onRootClick?: () => void;
    marginLeft?: string;
    pageKey?: string;
    pageName?: string;
};

const messages = defineMessages({
    languageStudio: {
        id: 'languageStudio.languageStudioSubHeader.languageStudio',
        defaultMessage: 'Language Studio'
    }
});

const { getClassName } = getStylistV2('LanguageStudioSubHeader');

const itemClassName = getClassName('item', important({ fontSize: '14px', fontWeight: 'normal' }));
const linkItemClassName = getClassName('linkItem', theme =>
    important({ color: theme.linkColor, fontSize: '14px', display: 'inline-block', height: '100%' })
);
const linkIconClassName = getClassName('linkIcon', important({ fontSize: '10px' }));

const breadcrumbStyles = classNamesFunction<IBreadcrumbStyleProps, IBreadcrumbStyles>()({
    item: itemClassName,
    chevron: linkIconClassName,
    itemLink: `${itemClassName} ${linkItemClassName}`
});

const StlyedBreadcrumb = styled(Breadcrumb)<{ marginLeft: string }>(props => ({
    marginLeft: props.marginLeft
}));

export const LanguageStudioSubHeader = (props: LanguageStudioSubHeaderProps) => {
    const { onRootClick, marginLeft = '0', pageKey, pageName } = props;
    const history = useHistory();
    const { localize } = useLocalization();

    const items: IBreadcrumbItem[] = [
        {
            isCurrentItem: !Boolean(pageName),
            key: 'languageStudio',
            text: localize(messages.languageStudio),
            onClick: () => {
                history.push(routeTable.languageStudio.home());
                onRootClick?.();
            }
        },
        ...(Boolean(pageName)
            ? [
                  {
                      isCurrentItem: true,
                      key: pageKey,
                      text: pageName
                  }
              ]
            : [])
    ];

    return <StlyedBreadcrumb items={items} marginLeft={marginLeft} styles={breadcrumbStyles} />;
};
